import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Spinner, Table, Input, Modal, ModalBody, ModalFooter } from 'reactstrap';
import moment from 'moment';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Translate, { t } from '../common/Translate';
import { DefaultAlertModal } from '../common/Modal/DefaultAlertModal';
import { useSaleParameters } from '../../hooks/useAutobizTrade/useSaleParameters';
import { currencySign } from '../../Utils/currencySign';
import { Auth } from '../../config/configuration/react';
import {
  REACT_APP_GROUP_CONFIG as countryGroups,
  REACT_APP_PUBLIC_GROUP_CONFIG as publicSaleGroup,
} from '../../config';
import counterOfferDisplay from '../../Utils/counterOfferDisplay';

const SaleParameters = ({
  pdv,
  tradeStatus,
  showBlocPending,
  sales,
  showParameters,
  offers,
  showOffers,
  offersTitle,
  offersLoading,
  offersErrorApi,
  supOfferId,
}) => {
  const {
    appLang,
    isFinished,
    error,
    loading,
    validateBtn,
    cancelBtn,
    sortFunction,
    toggle,
    handleCancel,
    handlePutWinner,
    onChangeWinner,
    onSortChange,
    setBody,
    body,
    merchantName,
    winner,
    isOpen,
    currentSortAmount,
    currentSortCreatedAt,
    sortTypes,
    counterOfferStatus,
    onChangeCounterOfferDetails,
    isOpenCounterOffer,
    offerAmount,
    counterOfferAmount,
    setBodyCounterOffer,
    bodyCounterOffer,
    changeCounterOfferAmount,
    toggleCounterOffer,
    handleCancelCounterOffer,
    handleSendCounterOffer,
    inferiorCounterOffer,
    counterOfferPending,
    tradeData,
    openRefuseOffersPopup,
    setOpenRefuseOffersPopup,
    toggleRefuseOffersPopup,
    handleRefuseAllOffers,
    saleId,
  } = useSaleParameters({
    sales,
    offers,
    tradeStatus,
  });
  const roleId = Auth.roleId();
  const hideMerchantInfoForUser = Auth.isAllowedToSeeMerchant();
  const recordId = useSelector((state) => state.reduxRecords?.currentRecord);
  const hideMerchantInfo = useSelector(
    (state) => state.reduxRecords.data[recordId]?.sourceInfos?.sourceGroupData?.hideMerchantInfo,
  );
  const displayOnlyWinnersInfo = useSelector(
    (state) => state.reduxRecords.data[recordId]?.sourceInfos?.sourceGroupData?.displayOnlyWinnersInfo,
  );
  const sortedOffers = offers ? [...offers].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : [];
  const saleStatus = tradeData?.sale?.sale_status?.name;
  const inGroupsIds = (tradeData?.sale?.distribution_lists || []).map((group) => group.id);
  const publicSale = (() => {
    const publicGroupConfig = publicSaleGroup;
    const nationalGroupConfig = Object.values(countryGroups || {});
    const combinedPublicGroups = [
      ...(Array.isArray(publicGroupConfig) ? publicGroupConfig : [publicGroupConfig]),
      ...nationalGroupConfig,
    ];
    return combinedPublicGroups.some((group) => inGroupsIds.includes(String(group)));
  })();
  const hideUserInfos = (winningOffer) => {
    return (
      tradeData?.sale?.sale_offer_type.name === 'stock' &&
      publicSale &&
      (saleStatus === 'live' ||
        (isFinished && (!tradeData?.sale?.winner || (tradeData?.sale?.winner && !winningOffer))))
    );
  };
  const blurring = (winningOffer) =>
    !publicSale &&
    (roleId === 1 || hideMerchantInfoForUser || hideMerchantInfo || (displayOnlyWinnersInfo && !winningOffer));

  useEffect(() => {
    if (isOpenCounterOffer) {
      setBodyCounterOffer(
        <>
          <div className="overflow-auto">
            <p className="font-weight-bold text-center">{t('textCounterOfferPopup')}</p>
            <span>
              <p className="font-weight-bold d-inline">{t('originalOffer')}</p>
              <p className="d-inline">{currencySign(`   ${offerAmount.toLocaleString()}`)}</p>
            </span>
            <span>
              <br />
              <br />
            </span>
            <span className="d-block">
              <p className="font-weight-bold d-inline">{t('amountCounterOffer')}&nbsp;&nbsp;&nbsp;</p>
              <Input
                type="number"
                className="form-control rounded counter-offer-input"
                value={counterOfferAmount}
                onChange={(e) => changeCounterOfferAmount(e)}
              />
              <p className="font-weight-bold d-inline">&nbsp;&nbsp;{currencySign()}</p>
            </span>
            {inferiorCounterOffer && (
              <span className="d-block">
                <p>
                  <small className="font-weight-bold text-danger">{t('counterOfferSupOriginialOffer')}</small>
                </p>
              </span>
            )}
          </div>
        </>,
      );
    }
  }, [inferiorCounterOffer, isOpenCounterOffer, counterOfferAmount]);

  return (
    <>
      {isOpen &&
        !body &&
        merchantName &&
        setBody(
          <>
            <div className="overflow-auto">
              <p className="font-weight-bold">{(winner && t('confirmationAttribution1')) || t('deleteAttribution')}</p>
              <p className="font-weight-bold">{winner && t('confirmationAttribution2')}</p>
              <p>{(winner && t('confirmationAttribution3')) || t('confirmationAttribution2')}</p>
              <p>{!winner && t('deleteAttribution2')}</p>
            </div>
          </>,
        )}
      {showParameters && (
        <>
          <Row className="mt-2">
            <Col xs="12" sm="6" lg="5" xl="4">
              <font className="text-primary text-left" size="5">
                <Translate code="sale_title" />
              </font>
            </Col>
          </Row>
          <Row>
            <Col xs="12" sm="6" lg="5" xl="4">
              <table className="sales-table">
                <tbody>
                  <tr>
                    <td>
                      <small className="text-primary">{t('saleStatus')}</small>
                    </td>

                    <td>
                      <small>{t(tradeStatus)}</small>
                    </td>
                  </tr>
                  {!showBlocPending && (
                    <>
                      <tr>
                        <td>
                          <small className="text-primary">{t('saleStartDateTime')}</small>
                        </td>

                        <td>
                          <small>
                            {moment(sales.start_date_time).format(
                              `${appLang === 'DE' ? 'DD.MM.YYYY' : 'DD/MM/YYYY'} HH:mm`,
                            )}
                          </small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary">{t('saleEndDateTime')}</small>
                        </td>

                        <td>
                          <small>
                            {moment(sales.end_date_time).format(
                              `${appLang === 'DE' ? 'DD.MM.YYYY' : 'DD/MM/YYYY'} HH:mm`,
                            )}
                          </small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary">{t('salePointOfSaleName')}</small>
                        </td>

                        <td>
                          <small>{pdv}</small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary font-weight-bold">{t('saleSubmission')}</small>
                        </td>

                        <td>
                          <small className="font-weight-bold">{(sales.accept_submission && t('yes')) || '-'}</small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary font-weight-bold">{t('saleAuction')}</small>
                        </td>

                        <td>
                          <small className="font-weight-bold">{(sales.accept_auction && t('yes')) || '-'}</small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary">{t('saleAuctionStartPrice')}</small>
                        </td>

                        <td>
                          <small>{(sales.accept_auction && currencySign(sales.auction_start_price)) || '-'}</small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary">{t('saleReservePrice')}</small>
                        </td>

                        <td>
                          <small>
                            {sales.accept_auction && sales.auction_reserve_price
                              ? currencySign(sales.auction_reserve_price)
                              : '-'}
                          </small>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <small className="text-primary font-weight-bold">{t('saleImmediatePurchase')}</small>
                        </td>

                        <td>
                          <small className="font-weight-bold">
                            {(sales.accept_immediate_purchase && currencySign(sales.immediate_purchase_price)) || '-'}
                          </small>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </Col>
          </Row>
          {!showBlocPending && (
            <Row className="mt-5">
              <Col xs="12" sm="6" lg="5" xl="4">
                <font className="text-primary text-left" size="5">
                  <Translate code={offersTitle} />
                </font>
              </Col>
            </Row>
          )}
        </>
      )}
      <>
        {(offersLoading || loading) && (
          <div className="mt-3">
            <br />
            <span className="text-primary text-left">
              <Translate code="getting_offers" /> ...
              <Spinner type="grow" color="primary" />
            </span>
          </div>
        )}
        {(offersErrorApi || error) && (
          <div className="mt-3">
            <p className="text-danger">
              <Translate code={offersErrorApi} />
            </p>
          </div>
        )}

        {showOffers && (
          <>
            {saleStatus === 'winner_to_designate' && (
              <button
                type="button"
                className="btn btn-1-outline refuseOffersBtn"
                style={{ color: '#dc3545', borderColor: '#dc3545', float: 'right' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenRefuseOffersPopup(true);
                }}
              >
                {t('refuseAllOffers')}
              </button>
            )}

            <Table bordered hover size="sm" responsive="sm" className="text-center subtitle offer-table-sales">
              <thead className="offer-table-sales-header">
                <tr>
                  <th>{t('merchantInfos')}</th>
                  <th>{t('email')}</th>
                  <th>{t('phoneNumber')}</th>
                  <th>
                    {t('offer_createdat')}
                    <button className="icon-sortable" type="button" onClick={() => onSortChange('lastOfferCreatedAt')}>
                      <FontAwesomeIcon icon={sortTypes.lastOfferCreatedAt[currentSortCreatedAt].icon} />
                    </button>
                  </th>
                  <th>
                    {t('stockOffer')}
                    <button className="icon-sortable" type="button" onClick={() => onSortChange('amount')}>
                      <FontAwesomeIcon icon={sortTypes.amount[currentSortAmount].icon} />
                    </button>
                  </th>
                  <th>{t('offer_saletype')}</th>
                  <th>{t('counterOffer')}</th>
                  <th>{t('stockAwarding')}</th>
                </tr>
              </thead>
              <tbody className="offer-table-sales-body">
                {sortedOffers &&
                  sortedOffers.sort(sortFunction).map((offer) => (
                    <tr key={offer.id}>
                      <td className={blurring(offer?.is_winner) && !hideUserInfos(offer?.is_winner) ? 'blur' : ''}>
                        {!hideUserInfos(offer?.is_winner) ? (
                          <>
                            {_.capitalize(_.get(offer, 'user.first_name', null))}{' '}
                            {_.capitalize(_.get(offer, 'user.last_name', null))}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </td>

                      <td className={blurring(offer?.is_winner) && !hideUserInfos(offer?.is_winner) ? 'blur' : ''}>
                        {!hideUserInfos(offer?.is_winner) ? _.get(offer, 'user.email', null) : <>-</>}
                      </td>

                      <td className={blurring(offer?.is_winner) && !hideUserInfos(offer?.is_winner) ? 'blur' : ''}>
                        {!hideUserInfos(offer?.is_winner) ? _.get(offer, 'user.phone_number', null) : <>-</>}
                      </td>
                      <td>
                        {moment(_.get(offer, 'created_at', null)).format(
                          `${appLang === 'DE' ? 'DD.MM.YYYY' : 'DD/MM/YYYY'} HH:mm`,
                        )}
                      </td>
                      <td className={blurring(offer?.is_winner) && !hideUserInfos(offer?.is_winner) ? 'blur' : ''}>
                        {currencySign(_.get(offer, 'amount', null))}
                        {isFinished && supOfferId === offer.id && (
                          <svg
                            className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall column-offer-sup-star"
                            focusable="false"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            data-testid="true"
                            width="20"
                            height="20"
                          >
                            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm4.24 16L12 15.45 7.77 18l1.12-4.81-3.73-3.23 4.92-.42L12 5l1.92 4.53 4.92.42-3.73 3.23L16.23 18z" />
                          </svg>
                        )}
                      </td>
                      <td>{t(_.get(offer, 'offer_type.name', null))}</td>
                      <td>
                        {counterOfferDisplay(
                          offer,
                          counterOfferStatus,
                          saleStatus,
                          winner,
                          onChangeCounterOfferDetails,
                        )}
                      </td>
                      <td className="align-middle">
                        {!(saleStatus === 'closed_no_purchase') && (
                          <div className="form-group-sales">
                            <Input
                              checked={winner?.id === offer.id}
                              id={offer.id}
                              type="checkbox"
                              disabled={
                                (winner && !offer.is_winner) ||
                                moment().format('YYYY-MM-DD HH:mm:ss') <=
                                  moment(offer.sale.end_date_time).format('YYYY-MM-DD HH:mm:ss') ||
                                counterOfferPending
                              }
                              className="input-sale"
                              onChange={(e) => onChangeWinner(e, offer)}
                            />
                            <label htmlFor={offer.id}>&nbsp;</label>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>

            <Modal isOpen={openRefuseOffersPopup} toggle={toggleRefuseOffersPopup}>
              <ModalBody className="offers-refuse-body-text">
                <p className="font-weight-normal">{t('refuseAllOffersPopup')}</p>
              </ModalBody>
              <ModalFooter className="popup-ctas">
                <div className="model-footer">
                  <button
                    className="btn btn-3 mr-2"
                    type="button"
                    style={{ width: '25%' }}
                    onClick={toggleRefuseOffersPopup}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    className="btn btn-1 ml-2"
                    type="button"
                    style={{ width: '25%' }}
                    onClick={() => handleRefuseAllOffers(saleId)}
                  >
                    {t('validate')}
                  </button>
                </div>
              </ModalFooter>
            </Modal>
          </>
        )}
        <DefaultAlertModal
          isOpen={isOpen}
          toggle={toggle}
          withHeader={false}
          body={body}
          cancelBtn={cancelBtn}
          validateBtn={validateBtn}
          handleCancel={handleCancel}
          handleValidate={handlePutWinner}
          centered
          size="md"
        />

        <DefaultAlertModal
          isOpen={isOpenCounterOffer}
          toggle={toggleCounterOffer}
          withHeader={false}
          body={bodyCounterOffer}
          cancelBtn={cancelBtn}
          validateBtn={validateBtn}
          handleCancel={handleCancelCounterOffer}
          handleValidate={handleSendCounterOffer}
          centered
          size="md"
        />
        {loading && (
          <div className="loader-full-screen main">
            <Spinner />
          </div>
        )}
      </>
    </>
  );
};

export default SaleParameters;

SaleParameters.propTypes = {
  pdv: PropTypes.string.isRequired,
  tradeStatus: PropTypes.string.isRequired,
  offersTitle: PropTypes.string.isRequired,
  offersErrorApi: PropTypes.bool.isRequired,
  showBlocPending: PropTypes.bool.isRequired,
  showParameters: PropTypes.bool.isRequired,
  offersLoading: PropTypes.bool.isRequired,
  showOffers: PropTypes.bool.isRequired,
  sales: PropTypes.shape().isRequired,
  offers: PropTypes.oneOfType([PropTypes.array]).isRequired,
  supOfferId: PropTypes.number,
};

SaleParameters.defaultProps = {
  supOfferId: null,
};
